import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import '@wsb/shelf/dist/index.css';
import React, { ComponentType } from 'react';
import { createApp } from '@godaddy/gasket-next';
import { withGasketDataProvider } from '@gasket/nextjs';
import { withThunderAuth } from '@wsb/gasket-thunder-auth-react';
import { withRoutes } from '@wsb/gasket-plugin-complex-nextjs-routes';
import { Theme } from '../theme';
import { createUseStyles } from '../hooks/jss';
import { AnalyticsClickTracker } from '../containers/AnalyticsClickTracker';
import { AnalyticsTrackerProvider } from '../containers/AnalyticsTrackerProvider';
import { useSelector } from 'react-redux';
import { ErrorCatch } from '../components/common/ErrorCatch';
import { BuildSHA } from '../components/common/BuildSHA';
import { GrowlProvider } from '../containers/GrowlProvider';
import { withAdvancedApmRum } from '@wsb/gasket-advanced-apm-rum/client';
import { getApiError } from '../redux/selectors/getApiError';
import { ErrorComponent } from './_error';
import { withTheme } from '../utils/hoc/withTheme';
import { GasketPageArgs } from '../types/GasketPageArgs';
import { withAnalyticsTracker } from '../utils/hoc/withAnalyticsTracker';

/**
 * Applied global css
 */
const useStyle = createUseStyles((theme: Theme) => ({
  '@global': {
    '.bg-scheduled': { backgroundColor: '#FADCD9' },
    '.bg-service_request': { backgroundColor: '#FFEEA9' },
    '.bg-danger-highlight': { backgroundColor: '#FFBBBB' },
    '.ux-app': {
      backgroundColor: theme.ux.box.backgroundColor
    }
  },
  'appContainer': {
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingInline: '1.5rem',
    maxWidth: 1368,
    width: '100%'
  }
}));

const Root = ({ Page, pageProps }: GasketPageArgs) => {
  const classes = useStyle();
  const apiError = useSelector(getApiError);

  return (
    <ErrorCatch>
      <GrowlProvider>
        <AnalyticsTrackerProvider {...pageProps}>
          <AnalyticsClickTracker {...pageProps} />
          <section className={classes.appContainer}>
            {apiError && <ErrorComponent statusCode={apiError.status ?? '500'} />}
            {!apiError && <Page {...pageProps} />}
            <BuildSHA />
          </section>
        </AnalyticsTrackerProvider>
      </GrowlProvider>
    </ErrorCatch>
  );
};

// Adding { strictMode: false } removes a lot of the react warnings in console to help
// w/ debuging, i.e. createAppComponent(Root, { strictMode: false });
// eslint-disable-next-line import/no-default-export
export default createApp({
  Layout: withGasketDataProvider()(
    withAnalyticsTracker()(
      withTheme()(
        withThunderAuth({ withGasketData: false })(
          withRoutes({ withGasketData: false })(
            withAdvancedApmRum({ withGasketData: false, sourceApp: 'gum-marketing' })(
              Root
            ) as ComponentType<unknown>
          ) as ComponentType<unknown>
        ) as ComponentType<unknown>
      ) as ComponentType<unknown>
    )
  ),
  strictMode: false
});
